// File: src/pages/reports/SalesPersonTargetList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Input, Button, Spinner, Badge } from "reactstrap";
import { FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

import { _Get } from "../import/ApiEndPoint/ApiCalling";
import {
  View_CustomerTarget_List,
  All_Users_List,
  PurchaseProductList_Product,
  view_create_order_history,
} from "../import/ApiEndPoint/Api";
import { CreateCustomerList } from "../import/ApiEndPoint/ApiCalling";

import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

import { baseURL } from "../import/ApiEndPoint/TestApi";
import { useNavigate } from "react-router-dom";

/* ===================== Tiny CSS ===================== */
const injectOnce = () => {
  if (document.getElementById("sp-inline-css")) return;
  const s = document.createElement("style");
  s.id = "sp-inline-css";
  s.textContent = `
    .sp-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .sp-card{ border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .sp-card-light{ border:1px solid #e5e7eb; border-radius:14px; background:#f8fafc; }
    .sp-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .sp-table{ width:100%; border-collapse:collapse; min-width:1200px; }
    .sp-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .sp-table tbody td{ padding:8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; font-size:14px; }
    .row-click{ cursor:pointer; }
    .row-active{ background:#eef2ff; }
    .muted{ color:#64748b; }
    .dl-menu{ position:absolute; right:0; top:32px; z-index:10; border:1px solid rgb(8, 91, 245); background:#fff; border-radius:10px; overflow:hidden; min-width:140px; box-shadow:0 12px 24px rgba(0,0,0,.12); }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f4f7f6; }
  `;
  document.head.appendChild(s);
};

/* ===================== Helpers ===================== */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const num = (v) => (isFinite(+v) ? +v : 0);

const monthNamesFull = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const monthNamesShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const now = new Date();
const currentFYStart =
  now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;

const buildFYOptions = (from = currentFYStart - 3, to = currentFYStart + 2) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

const buildFYMonths = (fyStartYear = currentFYStart) => {
  const out = [];
  for (let i = 0; i < 12; i++) {
    const mIndex = (3 + i) % 12; // Apr..Mar
    const year = fyStartYear + (mIndex < 3 ? 1 : 0);
    out.push({
      key: `m_${year}_${mIndex}`,
      monthIndex: mIndex,
      year,
      month: mIndex + 1, // 1..12
      full: monthNamesFull[mIndex],
      header: `${monthNamesShort[mIndex]} ${year}`,
      label: `${monthNamesFull[mIndex]}-${year}`,
    });
  }
  return out;
};

const prevMonth = (year, month /*1..12*/) => {
  if (month === 1)
    return {
      year: year - 1,
      month: 12,
      label: `${monthNamesFull[11]}-${year - 1}`,
    };
  return {
    year,
    month: month - 1,
    label: `${monthNamesFull[month - 2]}-${year}`,
  };
};

// Simple display name like CreateSalesPersonTarget
const getUserDisplayName = (u) => {
  if (!u) return "—";
  const fn = u.firstName || u.name || "";
  const ln = u.lastName || "";
  const full = `${fn} ${ln}`.trim();
  return full || u.username || u.UserName || u.email || "—";
};

// prefer stable sId, fallback to _id
const partyKey = (idObjOrStr) => {
  if (!idObjOrStr) return "";
  if (typeof idObjOrStr === "object")
    return String(idObjOrStr?.sId || idObjOrStr?._id || idObjOrStr?.id || "");
  return String(idObjOrStr);
};

// always try to resolve a human-readable Sales Person name from a user-like object
const salesPersonNameFromObj = (src) => {
  if (!src || typeof src !== "object") return "";
  return (
    src.salesPersonName ||
    getUserDisplayName(src) ||
    getUserDisplayName(src.salesPersonId)
  );
};

// SalesPerson IDs extraction (handles object or string, and collects multiple variants)
const spIdsFromVal = (val) => {
  const out = [];
  if (!val) return out;

  if (typeof val === "object") {
    const candidates = [
      val?._id,
      val?.id,
      val?.sId,
      val?.userId,
      val?.code,
      val?.uid,
    ];
    candidates.forEach((x) => {
      if (x !== null && x !== undefined && String(x).trim())
        out.push(String(x));
    });
  } else {
    if (String(val).trim()) out.push(String(val));
  }
  return Array.from(new Set(out));
};

const spNameFromDoc = (doc) => {
  if (!doc) return "";
  const n1 = doc?.salesPersonName;
  if (n1) return String(n1).trim();
  const n2 = salesPersonNameFromObj(doc?.salesPersonId);
  if (n2) return String(n2).trim();
  const n3 = salesPersonNameFromObj(doc);
  if (n3) return String(n3).trim();
  return "";
};

// robust match: by ids (preferred), fallback by name
const docMatchesSP = (doc, selectedIds = [], selectedName = "") => {
  if (!doc) return false;

  const idSet = new Set((selectedIds || []).map((x) => String(x)));

  // id match
  if (idSet.size) {
    const docIds = [
      ...spIdsFromVal(doc?.salesPersonId),
      ...spIdsFromVal(doc),
      ...spIdsFromVal(doc?.salesPersonId?._id),
      ...spIdsFromVal(doc?.salesPersonId?.sId),
    ];
    for (const id of docIds) {
      if (idSet.has(String(id))) return true;
    }
  }

  // name fallback
  const want = String(selectedName || "")
    .trim()
    .toLowerCase();
  if (want) {
    const got = String(spNameFromDoc(doc) || "")
      .trim()
      .toLowerCase();
    if (got && got === want) return true;
  }

  return false;
};

// resolve a robust product display name
const pickProductTitle = (p) =>
  p?.Product_Title ||
  p?.ProductTitle ||
  p?.Product_Name ||
  p?.ProductName ||
  p?.Product_Desc ||
  p?.name ||
  p?.Title ||
  p?.title ||
  "";

// Catalog map: key both _id and sId → product object
const buildProductMap = (catalog = []) => {
  const mp = new Map();
  catalog.forEach((p) => {
    const k1 = p?._id || p?.id;
    const k2 = p?.sId;
    if (k1) mp.set(String(k1), p);
    if (k2) mp.set(String(k2), p);
  });
  return mp;
};

// Build name cache from catalog + targets (productDetails)
const buildProductNameCache = (catalog = [], targets = []) => {
  const nameMap = new Map();

  // from catalog
  catalog.forEach((p) => {
    const nm = pickProductTitle(p);
    if (!nm) return;
    const id = p?._id || p?.id;
    const sid = p?.sId;
    if (id) nameMap.set(String(id), nm);
    if (sid) nameMap.set(String(sid), nm);
  });

  // from targets.productDetails (if present)
  (targets || []).forEach((t) => {
    (t?.products || []).forEach((line) => {
      const nm =
        pickProductTitle(line?.productDetails) || pickProductTitle(line);
      if (!nm) return;
      const id = line?.productId?._id || line?.productId?.id || line?.productId;
      const sid = line?.productId?.sId || line?.productDetails?.sId;
      if (id) nameMap.set(String(id), nm);
      if (sid) nameMap.set(String(sid), nm);
    });
  });

  return nameMap;
};

// For targets lines: a stable product key (prefer sId, fallback _id/string)
const prodKeyFromLine = (line) =>
  String(
    line?.productDetails?.sId ||
      line?.productId?.sId ||
      line?.productId?._id ||
      line?.productId?.id ||
      line?.productId ||
      "",
  );

// From order item
const prodKeyFromOrderItem = (it) =>
  String(it?.productId?.sId || it?.productId?._id || it?.productId || "");

// Compute amount from an order item
const amountFromItem = (it) => {
  const direct =
    it?.amount ??
    it?.lineAmount ??
    it?.lineTotal ??
    it?.total ??
    it?.Total ??
    null;
  if (direct != null) return num(direct);
  const qty = num(it?.qty || it?.quantity);
  const price = num(it?.rate || it?.price || it?.mrp || it?.SalesRate || 0);
  return qty * price;
};

/* ===================== Page ===================== */
export default function SalesPersonTargetList() {
  useEffect(() => injectOnce(), []);

  // UI
  const [loading, setLoading] = useState(true);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Data
  const [people, setPeople] = useState([]);
  const [selectedSPKey, setSelectedSPKey] = useState(""); // dropdown value
  const [selectedSPIds, setSelectedSPIds] = useState([]); // all ids for matching
  const [selectedSPName, setSelectedSPName] = useState(""); // fallback

  const [fyStartYear, setFyStartYear] = useState(currentFYStart);
  const monthsMeta = useMemo(() => buildFYMonths(fyStartYear), [fyStartYear]);
  const [selectedMonth, setSelectedMonth] = useState(null); // month meta object

  const [catalog, setCatalog] = useState([]);
  const [productMap, setProductMap] = useState(new Map());
  const [productNameMap, setProductNameMap] = useState(new Map());
  const [targets, setTargets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);

  // Summary + table
  const [monthsSummary, setMonthsSummary] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openKey, setOpenKey] = useState("");
  const [expandLoading, setExpandLoading] = useState(false);
  const [expandRows, setExpandRows] = useState([]);
  const [expandTitle, setExpandTitle] = useState("");

  // Second section: raw created Sales Person Targets (via fetch)
  const [spTargets, setSpTargets] = useState([]);

  // columns
  const baseColumns = useMemo(
    () => [
      { key: "uid", header: "UID", get: (_, i) => i + 1 },
      {
        key: "productName",
        header: "PRODUCT NAME",
        get: (r) => r.productName || r.productId,
      },
      { key: "targetQty", header: "TARGET QTY", get: (r) => r.targetQty },
      { key: "salePrice", header: "SALE PRICE", get: (r) => r.salePrice },
      {
        key: "productTotal",
        header: "PRODUCT TOTAL",
        get: (r) => r.productTotal,
      },
      {
        key: "cmTarget",
        header: "CURRENT MONTH TARGET",
        get: (r) => r.cmTarget,
      },
      {
        key: "cmAch",
        header: "CURRENT MONTH ACHIEVEMENT",
        get: (r) => r.cmAch,
      },
      {
        key: "cmShort",
        header: "CURRENT MONTH SHORTFALL",
        get: (r) => r.cmShort,
      },
      { key: "lmTarget", header: "LAST MONTH TARGET", get: (r) => r.lmTarget },
      { key: "lmAch", header: "LAST MONTH ACHIEVEMENT", get: (r) => r.lmAch },
      { key: "lmShort", header: "LAST MONTH SHORTFALL", get: (r) => r.lmShort },
      { key: "yTarget", header: "YEARLY TARGET", get: (r) => r.yTarget },
      { key: "yAch", header: "YEARLY ACHIEVEMENT", get: (r) => r.yAch },
      { key: "yShort", header: "YEARLY SHORTFALL", get: (r) => r.yShort },
    ],
    [],
  );

  const [selectedCols, setSelectedCols] = useState(
    baseColumns.map((c) => c.key),
  );
  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);
  const visibleCols = useMemo(
    () => selectedCols.map((k) => colMap.get(k)).filter(Boolean),
    [selectedCols, colMap],
  );

  // search filter (for automatic list table)
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r, i) =>
      visibleCols.some((c) => toStr(c.get(r, i)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols]);

  /* ======== Load core datasets ======== */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        const db = user?.database;

        // users (Sales Person) - keep wide so dropdown doesn't miss
        const uRes = await _Get(All_Users_List, db);
        const list = uRes?.User || uRes?.users || uRes?.adminDetails || [];
        const sales = (list || []).filter(
          (u) => u?.rolename?.roleName === "Sales Person",
        );
        setPeople(sales);

        // product catalog
        const pRes = await _Get(PurchaseProductList_Product, db);
        const cat = Array.isArray(pRes?.Product) ? pRes.Product : [];
        setCatalog(cat);
        const pMap = buildProductMap(cat);
        setProductMap(pMap);

        // customers
        const cRes = await CreateCustomerList(user?._id, db);
        const cus = cRes?.Customer || [];
        setCustomers(cus);

        // targets – used by automatic list
        const tRes = await _Get(View_CustomerTarget_List, db);
        const tgs = tRes?.data || [];
        setTargets(tgs);

        // orders (completed only)
        const oh = await _Get(view_create_order_history, user?._id, db);
        const completed = (oh?.orderHistory || [])
          .filter((x) => x?.status !== "Deactive")
          .filter((x) => String(x?.status || "").toLowerCase() === "completed");
        setOrders(completed);

        // Build product name cache
        setProductNameMap(buildProductNameCache(cat, tgs));

        // ===== Second section: use FETCH to load created Sales Person Targets =====
        if (db) {
          try {
            const res = await fetch(
              `${baseURL}/salesperson-targets?database=${encodeURIComponent(
                db,
              )}`,
              {
                method: "GET",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
              },
            );
            if (res.ok) {
              const json = await res.json();
              const list2 = Array.isArray(json?.data)
                ? json.data
                : Array.isArray(json)
                  ? json
                  : [];
              setSpTargets(list2);
            } else {
              setSpTargets(Array.isArray(tgs) ? tgs : []);
            }
          } catch (err) {
            console.error(
              "[SalesPersonTargetList] fetch salesperson-targets:",
              err,
            );
            setSpTargets(Array.isArray(tgs) ? tgs : []);
          }
        }
      } catch (e) {
        console.error("[SalesPersonTargetList] load error:", e);
        setPeople([]);
        setCatalog([]);
        setCustomers([]);
        setTargets([]);
        setOrders([]);
        setProductNameMap(new Map());
        setSpTargets([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ======== Sales Person dropdown (bring ALL sales persons created) ======== */
  const salesPersonOptions = useMemo(() => {
    // each group: { key, name, ids:Set }
    const groups = [];

    const tryMerge = (name, ids) => {
      const cleanName = String(name || "").trim();
      const idsArr = (ids || []).map((x) => String(x)).filter(Boolean);

      // find existing by id overlap
      let found = null;
      for (const g of groups) {
        for (const id of idsArr) {
          if (g.ids.has(id)) {
            found = g;
            break;
          }
        }
        if (found) break;
      }

      // fallback merge by same name
      if (!found && cleanName) {
        const low = cleanName.toLowerCase();
        found =
          groups.find((g) => String(g.name || "").toLowerCase() === low) ||
          null;
      }

      if (!found) {
        const primary = idsArr[0] || (cleanName ? `name:${cleanName}` : "");
        if (!primary) return;
        const g = {
          key: primary,
          name: cleanName || "—",
          ids: new Set(idsArr),
        };
        groups.push(g);
        return;
      }

      // merge into found
      if (cleanName && (!found.name || found.name === "—"))
        found.name = cleanName;
      idsArr.forEach((id) => found.ids.add(id));
    };

    // 1) from All Users list
    (people || []).forEach((u) => {
      const ids = [
        ...spIdsFromVal(u),
        ...spIdsFromVal(u?._id),
        ...spIdsFromVal(u?.sId),
      ];
      const name = getUserDisplayName(u);
      tryMerge(name, ids);
    });

    // 2) from targets docs
    (targets || []).forEach((t) => {
      const ids = [
        ...spIdsFromVal(t?.salesPersonId),
        ...spIdsFromVal(t),
        ...spIdsFromVal(t?.salesPersonId?._id),
        ...spIdsFromVal(t?.salesPersonId?.sId),
      ];
      const name = spNameFromDoc(t);
      if (name || ids.length) tryMerge(name || "—", ids);
    });

    // 3) from created sales person targets (fetch)
    (spTargets || []).forEach((t) => {
      const ids = [
        ...spIdsFromVal(t?.salesPersonId),
        ...spIdsFromVal(t),
        ...spIdsFromVal(t?.salesPersonId?._id),
        ...spIdsFromVal(t?.salesPersonId?.sId),
      ];
      const name = spNameFromDoc(t);
      if (name || ids.length) tryMerge(name || "—", ids);
    });

    // to dropdown options
    const opts = groups
      .map((g) => ({
        key: g.key,
        name: g.name || "—",
        ids: Array.from(g.ids),
      }))
      .filter((x) => x.key)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    return opts;
  }, [people, targets, spTargets]);

  const optionByKey = useMemo(() => {
    const mp = new Map();
    (salesPersonOptions || []).forEach((o) => mp.set(o.key, o));
    return mp;
  }, [salesPersonOptions]);

  /* ======== FY summary per SP (automatic list) ======== */
  const recomputeMonthsSummary = (fyYear) => {
    if (!selectedSPKey) {
      setMonthsSummary([]);
      return;
    }

    const fyLabels = buildFYMonths(fyYear).map((m) => m.label);
    const sumByLabel = new Map(fyLabels.map((l) => [l, 0]));

    (targets || [])
      .filter((t) => docMatchesSP(t, selectedSPIds, selectedSPName))
      .forEach((t) => {
        const lab = String(t?.date || "");
        if (!sumByLabel.has(lab)) return;
        const total = (t?.products || []).reduce(
          (acc, p) =>
            acc +
            (p?.totalPrice != null
              ? num(p.totalPrice)
              : num(p.qtyAssign) * num(p.price)),
          0,
        );
        sumByLabel.set(lab, sumByLabel.get(lab) + total);
      });

    const out = fyLabels.map((l) => ({
      label: l,
      grandTotal: sumByLabel.get(l) || 0,
    }));
    setMonthsSummary(out);
  };

  useEffect(() => {
    if (selectedSPKey) recomputeMonthsSummary(fyStartYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, selectedSPKey, selectedSPIds, selectedSPName, fyStartYear]);

  const monthAmountMap = useMemo(() => {
    const mp = new Map();
    (monthsSummary || []).forEach((x) =>
      mp.set(String(x.label), num(x.grandTotal)),
    );
    return mp;
  }, [monthsSummary]);

  /* ======== Event handlers ======== */
  const handleSPChange = (e) => {
    const key = e.target.value;

    setSelectedSPKey(key);
    setOpenKey("");
    setExpandRows([]);
    setRows([]);
    setExpandTitle("");

    // month stays selected if user already picked it
    const opt = optionByKey.get(key);
    const ids = opt?.ids || [];
    const name = opt?.name || "";

    setSelectedSPIds(ids);
    setSelectedSPName(name);

    if (!key) {
      setSelectedSPIds([]);
      setSelectedSPName("");
      setSelectedMonth(null);
      setMonthsSummary([]);
      return;
    }

    recomputeMonthsSummary(fyStartYear);
  };

  const handleFYChange = (e) => {
    const v = Number(e.target.value);
    setFyStartYear(v);

    // reset month + table when FY changes
    setSelectedMonth(null);
    setRows([]);
    setOpenKey("");
    setExpandRows([]);
    setExpandTitle("");

    if (selectedSPKey) recomputeMonthsSummary(v);
  };

  const handleMonthDropdown = async (e) => {
    const k = e.target.value;
    if (!k) {
      setSelectedMonth(null);
      setRows([]);
      setOpenKey("");
      setExpandRows([]);
      setExpandTitle("");
      return;
    }
    const meta = monthsMeta.find((x) => x.key === k) || null;
    setSelectedMonth(meta);
    setOpenKey("");
    setExpandRows([]);
    setExpandTitle("");
    if (selectedSPKey && meta) {
      await buildMonthMetrics(meta, fyStartYear);
    }
  };

  /* ======== Metrics (product-wise) – automatic list ======== */
  const getProductName = (key) => {
    const k = String(key || "");
    const fromCache = productNameMap.get(k);
    if (fromCache) return fromCache;
    const inCatalog = productMap.get(k);
    if (inCatalog) return pickProductTitle(inCatalog) || k;
    return k;
  };

  const buildMonthMetrics = async (mMeta, fyYear) => {
    if (!selectedSPKey || !mMeta) return;

    setMetricsLoading(true);
    try {
      const labelNow = mMeta.label;
      const { year: py, month: pm } = prevMonth(mMeta.year, mMeta.month);
      const labelPrev = `${monthNamesFull[pm - 1]}-${py}`;
      const fyLabels = buildFYMonths(fyYear).map((x) => x.label);

      const docsFY = (targets || []).filter(
        (t) =>
          docMatchesSP(t, selectedSPIds, selectedSPName) &&
          fyLabels.includes(String(t?.date || "")),
      );
      const docsNow = docsFY.filter((t) => String(t?.date || "") === labelNow);
      const docsPrev = docsFY.filter(
        (t) => String(t?.date || "") === labelPrev,
      );

      const add = (map, line) => {
        const key = prodKeyFromLine(line);
        if (!key) return;
        const qty = num(line?.qtyAssign);
        const price = num(line?.price);
        const amt =
          line?.totalPrice != null ? num(line.totalPrice) : qty * price;
        const prev = map.get(key) || { qty: 0, amt: 0, priceSum: 0, qtySum: 0 };
        map.set(key, {
          qty: prev.qty + qty,
          amt: prev.amt + amt,
          priceSum: prev.priceSum + price * qty,
          qtySum: prev.qtySum + qty,
        });
      };

      const agg = (docs) => {
        const map = new Map();
        for (const d of docs) for (const p of d?.products || []) add(map, p);
        return map;
      };

      const mapNow = agg(docsNow);
      const mapPrev = agg(docsPrev);
      const mapFY = agg(docsFY);

      const partiesNow = new Set(
        docsNow.map((d) => partyKey(d?.partyId)).filter(Boolean),
      );
      const partiesPrev = new Set(
        docsPrev.map((d) => partyKey(d?.partyId)).filter(Boolean),
      );
      const partiesFY = new Set(
        docsFY.map((d) => partyKey(d?.partyId)).filter(Boolean),
      );

      const achieve = (y, m, partySet) => {
        const out = new Map(); // prodKey -> {qty, amt}
        for (const ord of orders || []) {
          const d = ord?.date ? new Date(ord.date) : null;
          if (!d || d.getFullYear() !== y || d.getMonth() + 1 !== m) continue;

          const pKey = String(
            ord?.partyId?.sId ||
              ord?.partyId ||
              ord?.party?.sId ||
              ord?.customer?.sId ||
              "",
          );
          if (!pKey || !partySet.has(pKey)) continue;

          for (const it of ord?.orderItems || []) {
            const k = prodKeyFromOrderItem(it);
            if (!k) continue;
            const qty = num(it?.qty || it?.quantity);
            const amt = amountFromItem(it);
            const prev = out.get(k) || { qty: 0, amt: 0 };
            out.set(k, { qty: prev.qty + qty, amt: prev.amt + amt });
          }
        }
        return out;
      };

      const achNow = achieve(mMeta.year, mMeta.month, partiesNow);
      const achPrev = achieve(py, pm, partiesPrev);

      const achFY = new Map();
      for (const m of buildFYMonths(fyYear)) {
        const a = achieve(m.year, m.month, partiesFY);
        for (const [k, v] of a.entries()) {
          const prev = achFY.get(k) || { qty: 0, amt: 0 };
          achFY.set(k, { qty: prev.qty + v.qty, amt: prev.amt + v.amt });
        }
      }

      const keys = new Set([
        ...mapNow.keys(),
        ...mapPrev.keys(),
        ...mapFY.keys(),
        ...achNow.keys(),
        ...achPrev.keys(),
        ...achFY.keys(),
      ]);

      const out = [];
      for (const k of keys) {
        const n = mapNow.get(k) || { qty: 0, amt: 0, priceSum: 0, qtySum: 0 };
        const p = mapPrev.get(k) || { qty: 0, amt: 0, priceSum: 0, qtySum: 0 };
        const y = mapFY.get(k) || { qty: 0, amt: 0, priceSum: 0, qtySum: 0 };

        const avgPrice =
          n.qtySum > 0
            ? n.priceSum / n.qtySum
            : y.qtySum > 0
              ? y.priceSum / y.qtySum
              : 0;

        const aN = achNow.get(k) || { qty: 0, amt: 0 };
        const aP = achPrev.get(k) || { qty: 0, amt: 0 };
        const aY = achFY.get(k) || { qty: 0, amt: 0 };

        out.push({
          productId: k,
          productName: getProductName(k),
          targetQty: n.qty,
          salePrice: Number(avgPrice.toFixed(2)),
          productTotal: n.amt,

          cmTarget: n.amt,
          lmTarget: p.amt,
          yTarget: y.amt,

          cmAch: aN.amt,
          lmAch: aP.amt,
          yAch: aY.amt,

          cmShort: Math.max(n.amt - aN.amt, 0),
          lmShort: Math.max(p.amt - aP.amt, 0),
          yShort: Math.max(y.amt - aY.amt, 0),
        });
      }

      out.sort((a, b) =>
        String(a.productName).localeCompare(String(b.productName)),
      );
      setRows(out);
    } catch (e) {
      console.error("[SalesPersonTargetList] metrics error:", e);
      setRows([]);
    } finally {
      setMetricsLoading(false);
    }
  };

  /* ======== Expand product → customer breakdown (with names) ======== */
  const toggleExpand = async (row) => {
    const key = String(row.productId);
    if (!selectedSPKey || !selectedMonth) return;

    if (openKey === key) {
      setOpenKey("");
      setExpandRows([]);
      setExpandTitle("");
      return;
    }

    setOpenKey(key);
    setExpandLoading(true);
    setExpandRows([]);
    setExpandTitle(`${row.productName} — ${selectedMonth.label}`);

    try {
      const labelNow = selectedMonth.label;
      const { year: py, month: pm } = prevMonth(
        selectedMonth.year,
        selectedMonth.month,
      );
      const labelPrev = `${monthNamesFull[pm - 1]}-${py}`;

      const docsNow = (targets || []).filter(
        (t) =>
          docMatchesSP(t, selectedSPIds, selectedSPName) &&
          String(t?.date || "") === labelNow,
      );
      const docsPrev = (targets || []).filter(
        (t) =>
          docMatchesSP(t, selectedSPIds, selectedSPName) &&
          String(t?.date || "") === labelPrev,
      );

      const perPartyTarget = new Map();
      for (const d of docsNow) {
        const pk = partyKey(d?.partyId);
        if (!pk) continue;
        for (const p of d?.products || []) {
          const k = prodKeyFromLine(p);
          if (k !== key) continue;
          const qty = num(p?.qtyAssign);
          const price = num(p?.price);
          const amt = p?.totalPrice != null ? num(p.totalPrice) : qty * price;
          const prev = perPartyTarget.get(pk) || { qty: 0, amt: 0, price: 0 };
          perPartyTarget.set(pk, {
            qty: prev.qty + qty,
            amt: prev.amt + amt,
            price: price || prev.price,
          });
        }
      }

      const nameMap = new Map();
      (customers || []).forEach((c) => {
        const nm = c?.CompanyName || c?.companyName || c?.name || "—";
        const sId = c?.sId ? String(c.sId) : "";
        const id = c?._id ? String(c._id) : "";
        if (sId) nameMap.set(sId, nm);
        if (id) nameMap.set(id, nm);
      });
      docsNow.forEach((d) => {
        const pid = partyKey(d?.partyId);
        if (pid && d?.partyName) nameMap.set(String(pid), d.partyName);
      });

      const achieveParty = (y, m) => {
        const out = new Map();
        for (const ord of orders || []) {
          const d = ord?.date ? new Date(ord.date) : null;
          if (!d || d.getFullYear() !== y || d.getMonth() + 1 !== m) continue;

          const pk = String(
            ord?.partyId?.sId ||
              ord?.partyId ||
              ord?.party?.sId ||
              ord?.customer?.sId ||
              "",
          );
          if (!pk || !perPartyTarget.has(pk)) continue;

          for (const it of ord?.orderItems || []) {
            const k = prodKeyFromOrderItem(it);
            if (k !== key) continue;
            const qty = num(it?.qty || it?.quantity);
            const amt = amountFromItem(it);
            const prev = out.get(pk) || { qty: 0, amt: 0 };
            out.set(pk, { qty: prev.qty + qty, amt: prev.amt + amt });
          }
        }
        return out;
      };

      const achNow = achieveParty(selectedMonth.year, selectedMonth.month);
      const achPrev = achieveParty(py, pm);

      const perPartyPrevTarget = new Map();
      for (const d of docsPrev) {
        const pk = partyKey(d?.partyId);
        if (!pk) continue;
        for (const p of d?.products || []) {
          const k = prodKeyFromLine(p);
          if (k !== key) continue;
          const qty = num(p?.qtyAssign);
          const price = num(p?.price);
          const amt = p?.totalPrice != null ? num(p.totalPrice) : qty * price;
          const prev = perPartyPrevTarget.get(pk) || {
            qty: 0,
            amt: 0,
            price: 0,
          };
          perPartyPrevTarget.set(pk, {
            qty: prev.qty + qty,
            amt: prev.amt + amt,
            price: price || prev.price,
          });
        }
      }

      const detail = [];
      for (const [pk, t] of perPartyTarget.entries()) {
        const nm = nameMap.get(pk) || pk;
        const a = achNow.get(pk) || { qty: 0, amt: 0 };
        const shortQty = Math.max(num(t.qty) - num(a.qty), 0);
        const price = t.price || (num(t.qty) ? num(t.amt) / num(t.qty) : 0);
        const shortAmt = shortQty * price;

        const pT = perPartyPrevTarget.get(pk) || { qty: 0, amt: 0, price };
        const pA = achPrev.get(pk) || { qty: 0, amt: 0 };
        const prevShortQty = Math.max(num(pT.qty) - num(pA.qty), 0);
        const prevShortAmt = prevShortQty * (pT.price || price);

        detail.push({
          partyId: pk,
          partyName: nm,
          targetQty: num(t.qty),
          targetAmt: num(t.amt),
          achievedQty: num(a.qty),
          achievedAmt: num(a.amt),
          shortfallQty: shortQty,
          shortfallAmt: shortAmt,
          lastMonthShortfallQty: prevShortQty,
          lastMonthShortfallAmt: prevShortAmt,
        });
      }

      detail.sort((a, b) =>
        String(a.partyName).localeCompare(String(b.partyName)),
      );
      setExpandRows(detail);
    } catch (e) {
      console.error("[SalesPersonTargetList] expand error:", e);
      setExpandRows([]);
    } finally {
      setExpandLoading(false);
    }
  };

  /* ======== Second section: filter created Sales Person Targets ======== */
  const filteredSpTargets = useMemo(() => {
    const baseList = spTargets && spTargets.length ? spTargets : targets || [];
    if (!selectedSPKey) return [];
    const bySP = baseList.filter((t) =>
      docMatchesSP(t, selectedSPIds, selectedSPName),
    );
    if (selectedMonth) {
      return bySP.filter((t) => String(t?.date || "") === selectedMonth.label);
    }
    return bySP;
  }, [
    spTargets,
    targets,
    selectedSPKey,
    selectedSPIds,
    selectedSPName,
    selectedMonth,
  ]);

  /* ======== Downloads ======== */
  const buildCSV = () => {
    const header = visibleCols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r, i) =>
      visibleCols
        .map((c) => `"${toStr(c.get(r, i)).replace(/"/g, '""')}"`)
        .join(","),
    );
    return [header, ...lines].join("\r\n");
  };
  const exportPDF = async () => {
    await exportDataToPDF(buildCSV(), "SalesPersonTargetList");
  };
  const exportCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SalesPersonTargetList.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  const exportXLS = async () => {
    await convertDataCSVtoExcel(buildCSV(), "SalesPersonTargetList");
  };
  const exportXLSX = async () => {
    await exportDataToExcel(buildCSV(), "SalesPersonTargetList");
  };
  const exportXML = async () => {
    await convertDataCsvToXml(buildCSV(), "SalesPersonTargetList");
  };

  /* ======== Render ======== */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  return (
    <>
      {/* Toolbar (NO "Sales Person Target List" count pill title, Month dropdown moved here) */}
      <Row className="sp-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="sp-card-light" style={{ padding: 10 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* 1) Title changed (no count pill) */}
              {/* <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Sales Person Targets
              </div> */}

              {/* Sales Person */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="muted" style={{ fontWeight: 700 }}>
                  Sales Person:
                </span>
                <Input
                  type="select"
                  style={{ height: 32, minWidth: 260, background: "#fff" }}
                  value={selectedSPKey}
                  onChange={handleSPChange}
                >
                  <option value="">-- Select --</option>
                  {salesPersonOptions.map((sp) => (
                    <option key={sp.key} value={sp.key}>
                      {sp.name}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Financial Year */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="muted" style={{ fontWeight: 700 }}>
                  Financial Year:
                </span>
                <select
                  value={fyStartYear}
                  onChange={handleFYChange}
                  style={{ height: 32, width: 160 }}
                  title="Financial Year (Apr–Mar)"
                >
                  {buildFYOptions().map((y) => (
                    <option key={y} value={y}>
                      {y}-{String(y + 1).slice(-2)}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2) Month dropdown moved to header */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="muted" style={{ fontWeight: 700 }}>
                  Month:
                </span>
                <Input
                  type="select"
                  style={{ height: 32, minWidth: 200, background: "#fff" }}
                  value={selectedMonth ? selectedMonth.key : ""}
                  onChange={handleMonthDropdown}
                  disabled={!selectedSPKey}
                  title="Select Month"
                >
                  <option value="">
                    {selectedSPKey
                      ? "-- Select --"
                      : "Select Sales Person first"}
                  </option>
                  {monthsMeta.map((m) => {
                    const amt = monthAmountMap.get(m.label) || 0;
                    const showAmt = selectedSPKey
                      ? ` • ${Number(amt).toLocaleString()}`
                      : "";
                    return (
                      <option key={m.key} value={m.key}>
                        {m.header}
                        {showAmt}
                      </option>
                    );
                  })}
                </Input>
              </div>

              <div style={{ flex: "1 1 120px" }} />

              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ background: "#fff", width: 240, height: 32 }}
              />

              <Button
                onClick={() => navigate("/app/sales-person-target/create")}
              >
                Create Sales Person Target
              </Button>

              <Button
                outline
                color="primary"
                title="Filter Columns"
                style={{ height: 32 }}
                onClick={() => {
                  setSelectedCols((prev) =>
                    prev.length === baseColumns.length
                      ? baseColumns
                          .filter((c) =>
                            [
                              "uid",
                              "productName",
                              "cmTarget",
                              "cmAch",
                              "cmShort",
                            ].includes(c.key),
                          )
                          .map((c) => c.key)
                      : baseColumns.map((c) => c.key),
                  );
                }}
              >
                <FaFilter size={14} />
              </Button>

              <div
                onMouseEnter={() => setIsDownloadOpen(true)}
                onMouseLeave={() => setIsDownloadOpen(false)}
                style={{ position: "relative" }}
              >
                <ImDownload
                  style={{ cursor: "pointer" }}
                  title="download file"
                  size={24}
                  color="rgb(8, 91, 245)"
                />
                {isDownloadOpen && (
                  <div className="dl-menu">
                    <div className="dl-item" onClick={exportPDF}>
                      .PDF
                    </div>
                    <div className="dl-item" onClick={exportCSV}>
                      .CSV
                    </div>
                    <div className="dl-item" onClick={exportXLS}>
                      .XLS
                    </div>
                    <div className="dl-item" onClick={exportXLSX}>
                      .XLSX
                    </div>
                    <div className="dl-item" onClick={exportXML}>
                      .XML
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table – Automatic, computed list (Section 1) */}
      <Row>
        <Col sm="12">
          <Card className="sp-card" style={{ padding: 8 }}>
            {!selectedSPKey ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                Choose a sales person above.
              </div>
            ) : !selectedMonth ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                Select a month from the Month dropdown.
              </div>
            ) : metricsLoading ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                <Spinner size="sm" color="primary" /> Loading metrics...
              </div>
            ) : filteredRows.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No data for selected month.
              </div>
            ) : (
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => {
                      const active = openKey === String(r.productId);
                      return (
                        <React.Fragment key={r.productId || i}>
                          <tr
                            className={`row-click ${active ? "row-active" : ""}`}
                            onClick={() => toggleExpand(r)}
                            title="Click to view customer-wise breakdown"
                          >
                            {visibleCols.map((c) => {
                              const val = c.get(r, i);
                              const numeric = [
                                "targetQty",
                                "salePrice",
                                "productTotal",
                                "cmTarget",
                                "lmTarget",
                                "yTarget",
                                "cmAch",
                                "lmAch",
                                "yAch",
                                "cmShort",
                                "lmShort",
                                "yShort",
                              ].includes(c.key);
                              return (
                                <td
                                  key={c.key}
                                  style={{
                                    fontWeight:
                                      c.key === "productName" ? 700 : 500,
                                  }}
                                >
                                  {numeric
                                    ? Number(val || 0).toLocaleString()
                                    : toStr(val)}
                                </td>
                              );
                            })}
                          </tr>

                          {active && (
                            <tr>
                              <td
                                colSpan={visibleCols.length}
                                style={{ background: "#f8fafc" }}
                              >
                                <div
                                  style={{ fontWeight: 700, marginBottom: 8 }}
                                >
                                  {expandTitle}
                                </div>

                                {expandLoading ? (
                                  <div
                                    style={{ padding: 12, color: "#6b7280" }}
                                  >
                                    <Spinner size="sm" /> Loading breakdown…
                                  </div>
                                ) : expandRows.length === 0 ? (
                                  <div
                                    style={{ padding: 12, color: "#6b7280" }}
                                  >
                                    No customer-level data.
                                  </div>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-sm">
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              minWidth: 220,
                                              textAlign: "left",
                                            }}
                                          >
                                            Customer
                                          </th>
                                          <th>Target Qty</th>
                                          <th>Target Amount</th>
                                          <th>Achieved Qty</th>
                                          <th>Achieved Amount</th>
                                          <th>Shortfall Qty</th>
                                          <th>Shortfall Amount</th>
                                          <th>Last Month Shortfall Qty</th>
                                          <th>Last Month Shortfall Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {expandRows.map((d, idx) => (
                                          <tr key={idx}>
                                            <td style={{ textAlign: "left" }}>
                                              {d.partyName || d.partyId}
                                            </td>
                                            <td>
                                              {Number(
                                                d.targetQty || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.targetAmt || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.achievedQty || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.achievedAmt || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.shortfallQty || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.shortfallAmt || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.lastMonthShortfallQty || 0,
                                              ).toLocaleString()}
                                            </td>
                                            <td>
                                              {Number(
                                                d.lastMonthShortfallAmt || 0,
                                              ).toLocaleString()}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ================= SECOND SECTION ================= */}
      {/* Pure list of created Sales Person Targets (using FETCH data) */}
      <Row style={{ marginTop: 16 }}>
        <Col sm="12">
          <Card className="sp-card" style={{ padding: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 16,
                }}
              >
                Created Sales Person Targets{" "}
                <span className="count-pill">{filteredSpTargets.length}</span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Direct list of saved Sales Person Target documents (via{" "}
                <code>fetch</code>).
              </div>
            </div>

            {!selectedSPKey ? (
              <div
                style={{ padding: 16, textAlign: "center", color: "#6b7280" }}
              >
                Select a Sales Person above to see their created targets.
              </div>
            ) : filteredSpTargets.length === 0 ? (
              <div
                style={{ padding: 16, textAlign: "center", color: "#6b7280" }}
              >
                No created Sales Person Target found for current selection.
              </div>
            ) : (
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Sales Person</th>
                      <th>Party</th>
                      <th>Financial Year</th>
                      <th>Target Month</th>
                      <th>Date Label</th>
                      <th>Grand Total</th>
                      <th>Increment %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSpTargets.map((t, idx) => {
                      const spName = spNameFromDoc(t) || selectedSPName || "—";

                      const party =
                        t?.partyName ||
                        (t?.partyId && t?.partyId?.CompanyName) ||
                        (t?.partyId && t?.partyId?.companyName) ||
                        (t?.partyId && t?.partyId?.name) ||
                        t?.partyId ||
                        "—";

                      return (
                        <tr key={t?._id || idx}>
                          <td>{idx + 1}</td>
                          <td>{spName}</td>
                          <td>{party}</td>
                          <td>{t?.financialYear || "—"}</td>
                          <td>{t?.monthName || "—"}</td>
                          <td>{t?.date || "—"}</td>
                          <td>{Number(t?.grandTotal || 0).toLocaleString()}</td>
                          <td>{Number(t?.incrementPercent || 0)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
